import {ActionIcon, createStyles, type CSSObject} from '@mantine/core';
import React, {useCallback, useEffect, useRef, useState} from 'react';

import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {
  REDESIGN_HEADER_ICON_CHIP_BACKGROUND,
  REDESIGN_HEADER_ICON_CHIP_RADIUS_PX,
  REDESIGN_HEADER_ICON_CHIP_SIZE_PX,
} from '../../ui/AIStyleConstants';
import {resetRightSidebar} from '../AISidepanelV2GlobalClientState';
import {redesignHeaderIconChipStyles} from './RedesignHeaderIconChip';

const useStyles = createStyles((theme) => ({
  root: {
    position: 'relative',
    display: 'inline-flex',
    width: REDESIGN_HEADER_ICON_CHIP_SIZE_PX,
    height: REDESIGN_HEADER_ICON_CHIP_SIZE_PX,
    // Own stacking context, so the panel's negative z-index keeps it behind the chevron rather than
    // escaping to an ancestor and hiding behind the header's background.
    zIndex: 0,
  },
  trigger: redesignHeaderIconChipStyles,
  // While the panel is open the chevron stays filled even if the pointer has moved down to the close,
  // and its bottom corners flatten into the panel's top edge.
  triggerOpen: {
    '&&': {
      backgroundColor: REDESIGN_HEADER_ICON_CHIP_BACKGROUND,
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    },
  },
  // The surface of the whole pill, not just the part below the chevron: it starts at the chevron's own
  // top edge (which its top padding leaves empty) and extends past it to hold the close, so one
  // Nucleus shadow wraps both cells. Sitting behind the chevron is what keeps the glyph from moving —
  // the chevron stays exactly where it renders when closed.
  panel: {
    position: 'absolute',
    top: 0,
    right: 0,
    zIndex: -1,
    width: REDESIGN_HEADER_ICON_CHIP_SIZE_PX,
    paddingTop: REDESIGN_HEADER_ICON_CHIP_SIZE_PX,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.white,
    borderRadius: REDESIGN_HEADER_ICON_CHIP_RADIUS_PX,
    boxShadow: theme.shadows.xs,
    overflow: 'hidden',
  },
  // The panel clips its own rounded corners, so the cell fills it squarely. Its top border is the
  // seam between the two cells.
  panelButton: {
    ...redesignHeaderIconChipStyles,
    '&&': {
      ...(redesignHeaderIconChipStyles['&&'] as CSSObject),
      borderRadius: 0,
      borderTop: `1px solid ${theme.other.border.defaultColor}`,
    },
  },
}));

/**
 * Grace period before a pointer leaving the control collapses the panel, so crossing the seam between
 * the chevron and the close (which the browser reports as leaving the chevron) doesn't yank the close
 * out from under the cursor.
 *
 * @visibleForTesting
 */
export const PANEL_CLOSE_DELAY_MS = 150;

export interface RedesignPanelToggleProps {
  /** Whether the sidepanel is fully expanded (wide view). */
  expanded: boolean;
}

/**
 * Redesign expand/collapse/close control. The chevron matches the header's other icon chips; hovering
 * it extends the chip downward into a pill holding the close, which is the only action the chevron
 * itself can't perform.
 */
export default function RedesignPanelToggle({expanded}: RedesignPanelToggleProps): JSX.Element {
  const {classes, cx} = useStyles();
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  const closeTimeoutRef = useRef<number | null>(null);

  const cancelScheduledClose = useCallback(() => {
    if (closeTimeoutRef.current != null) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);
  useEffect(() => cancelScheduledClose, [cancelScheduledClose]);

  const openPanel = useCallback(() => {
    cancelScheduledClose();
    setIsPanelOpen(true);
  }, [cancelScheduledClose]);
  const scheduleClosePanel = useCallback(() => {
    cancelScheduledClose();
    closeTimeoutRef.current = window.setTimeout(() => {
      closeTimeoutRef.current = null;
      setIsPanelOpen(false);
    }, PANEL_CLOSE_DELAY_MS);
  }, [cancelScheduledClose]);
  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLDivElement>) => {
      // Tabbing from the chevron to the close is a blur on the chevron; only collapse the panel once
      // focus has left the control entirely.
      if (!event.currentTarget.contains(event.relatedTarget)) {
        scheduleClosePanel();
      }
    },
    [scheduleClosePanel]
  );
  const handleUnexpand = useCallback(() => {
    resetRightSidebar();
    SidepanelActions.unexpand();
  }, []);
  const handleExpand = useCallback(() => {
    SidepanelActions.expand();
  }, []);
  const handleClose = useCallback(() => {
    SidepanelActions.close();
  }, []);
  const triggerLabel = expanded ? 'Collapse' : 'Expand';

  return (
    <div
      className={classes.root}
      onMouseEnter={openPanel}
      onMouseLeave={scheduleClosePanel}
      // Keyboard users get the same affordance: focusing the chevron reveals the close below it.
      onFocus={openPanel}
      onBlur={handleBlur}
    >
      <ActionIcon
        aria-label={triggerLabel}
        variant="subtle"
        size="md"
        className={cx(classes.trigger, isPanelOpen && classes.triggerOpen)}
        onClick={expanded ? handleUnexpand : handleExpand}
      >
        <BenchlingIcon icon={expanded ? 'chevrons-left' : 'chevrons-right'} />
      </ActionIcon>
      {isPanelOpen && (
        // Entering the panel cancels the close the chevron's own mouseleave just scheduled.
        <div className={classes.panel} onMouseEnter={openPanel}>
          <ActionIcon
            aria-label="Close"
            variant="subtle"
            size="md"
            className={classes.panelButton}
            onClick={handleClose}
          >
            <BenchlingIcon icon="xmark" />
          </ActionIcon>
        </div>
      )}
    </div>
  );
}
